import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import apiClient from '../../utils/apiClient';
import { RobotMap, MapAnnotations } from '../../types';

interface MapsState {
  maps: RobotMap[];
  selectedMap: RobotMap | null;
  isLoading: boolean;
  isSlamActive: boolean;
  error: string | null;
}

const initialState: MapsState = {
  maps: [],
  selectedMap: null,
  isLoading: false,
  isSlamActive: false,
  error: null,
};

export const fetchRobotMaps = createAsyncThunk<RobotMap[], string, { rejectValue: string }>(
  'maps/fetchRobotMaps',
  async (robotId, { rejectWithValue }) => {
    try {
      const response = await apiClient.get<RobotMap[]>(`/maps/robot/${robotId}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch maps');
    }
  }
);

export const uploadMap = createAsyncThunk<RobotMap, { robotId: string; name: string; pgmFile: File; yamlFile: File; keepoutPgmFile?: File; keepoutYamlFile?: File; waypointsFile?: File }, { rejectValue: string }>(
  'maps/uploadMap',
  async ({ robotId, name, pgmFile, yamlFile, keepoutPgmFile, keepoutYamlFile, waypointsFile }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('name', name);
      formData.append('pgm_file', pgmFile);
      formData.append('yaml_file', yamlFile);
      if (keepoutPgmFile) formData.append('keepout_pgm_file', keepoutPgmFile);
      if (keepoutYamlFile) formData.append('keepout_yaml_file', keepoutYamlFile);
      if (waypointsFile) formData.append('waypoints_file', waypointsFile);
      const response = await apiClient.post<RobotMap>(`/maps/robot/${robotId}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to upload map');
    }
  }
);

export const updateMapAnnotations = createAsyncThunk<RobotMap, { mapId: string; annotations: MapAnnotations }, { rejectValue: string }>(
  'maps/updateAnnotations',
  async ({ mapId, annotations }, { rejectWithValue }) => {
    try {
      const response = await apiClient.patch<RobotMap>(`/maps/${mapId}`, { annotations });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to update annotations');
    }
  }
);

export const deleteMap = createAsyncThunk<string, string, { rejectValue: string }>(
  'maps/deleteMap',
  async (mapId, { rejectWithValue }) => {
    try {
      await apiClient.delete(`/maps/${mapId}`);
      return mapId;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to delete map');
    }
  }
);

export const startSlam = createAsyncThunk<void, string, { rejectValue: string }>(
  'maps/startSlam',
  async (robotId, { rejectWithValue }) => {
    try { await apiClient.post(`/maps/robot/${robotId}/slam/start`); }
    catch (error: any) { return rejectWithValue(error.response?.data?.detail || 'Failed to start SLAM'); }
  }
);

export const stopSlam = createAsyncThunk<void, string, { rejectValue: string }>(
  'maps/stopSlam',
  async (robotId, { rejectWithValue }) => {
    try { await apiClient.post(`/maps/robot/${robotId}/slam/stop`); }
    catch (error: any) { return rejectWithValue(error.response?.data?.detail || 'Failed to stop SLAM'); }
  }
);

export const saveSlamMap = createAsyncThunk<{ mapId: string }, { robotId: string; name: string }, { rejectValue: string }>(
  'maps/saveSlamMap',
  async ({ robotId, name }, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(`/maps/robot/${robotId}/slam/save?name=${name}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to save SLAM map');
    }
  }
);

export const saveMapToRobot = createAsyncThunk<void, { robotId: string; mapId: string }, { rejectValue: any }>(
  'maps/saveMapToRobot',
  async ({ robotId, mapId }, { rejectWithValue }) => {
    try { await apiClient.post(`/maps/robot/${robotId}/save-to-robot/${mapId}`); }
    catch (error: any) { 
      const detail = error.response?.data?.detail;
      return rejectWithValue(typeof detail === 'string' ? detail : detail?.message || 'Failed to save map to robot'); 
    }
  }
);

const mapsSlice = createSlice({
  name: 'maps',
  initialState,
  reducers: {
    setSelectedMap: (state, action: PayloadAction<RobotMap | null>) => { state.selectedMap = action.payload; },
    clearError: (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRobotMaps.pending, (state) => { state.isLoading = true; state.error = null; })
      .addCase(fetchRobotMaps.fulfilled, (state, action) => { state.isLoading = false; state.maps = action.payload; })
      .addCase(fetchRobotMaps.rejected, (state, action) => { state.isLoading = false; state.error = action.payload || 'Failed'; })
      .addCase(uploadMap.fulfilled, (state, action) => { state.maps.unshift(action.payload); })
      .addCase(updateMapAnnotations.fulfilled, (state, action) => {
        const idx = state.maps.findIndex((m) => m.id === action.payload.id);
        if (idx !== -1) state.maps[idx] = action.payload;
        if (state.selectedMap?.id === action.payload.id) state.selectedMap = action.payload;
      })
      .addCase(deleteMap.fulfilled, (state, action) => {
        state.maps = state.maps.filter((m) => m.id !== action.payload);
        if (state.selectedMap?.id === action.payload) state.selectedMap = null;
      })
      .addCase(startSlam.fulfilled, (state) => { state.isSlamActive = true; })
      .addCase(stopSlam.fulfilled, (state) => { state.isSlamActive = false; });
  },
});

export const { setSelectedMap, clearError } = mapsSlice.actions;
export default mapsSlice.reducer;
